/*
 * COPYRIGHT: EUMETSAT 2018
 * LICENSE: PROPRIETARY
 * PROJECT: WMS
 *
 * All information contained herein is, and remains the property of EUMETSAT.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * EUMETSAT.
 */

import { Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { forkJoin } from 'rxjs/observable/forkJoin';

import { UserService, AuthResourceHandler } from '../../services';
import { ToastService } from '../../../layout/toast/toast.service';
import { ConfirmDialogService } from '../../../layout/configrm-dialog/confirm-dialog.service';
import { Wso2ApiService } from '../../services';
import { ConfigService }  from '../../../config/config.service';

enum InitStatus {
    Loading = 0,
    Ready = 1,
    Error = 2,
}

enum ApiSubscriptionStatus {
    Subscribed = 1,
    NotSubscribed = 2,
    SubscriptionError = 4
}


@Component({
    selector: 'app-root',
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.scss'],
    host: {'class': 'app-root'},
    encapsulation: ViewEncapsulation.None
})
export class RootComponent  {

    public InitStatus = InitStatus;

    public status = InitStatus.Loading;
    public appData;
    public activeTab = 'keys';

    constructor(
        public userService: UserService,
        private authResourceHandler: AuthResourceHandler,
        private confirmDialogService: ConfirmDialogService,
        private toastService: ToastService,
        private apiService: Wso2ApiService,
        private configService: ConfigService
    ) {

        userService.isAuthInitSuccess().then((success) => {
            if (!success) {
                toastService.showMessage(
                    'Unable to communicate with authorization service.', {
                        type: 'error',
                        sticky: true
                    }
                );
                this.status = InitStatus.Error;
            } else {
                this.apiService.getOrCreateUserApplication().subscribe((app) => {
                    this.appData = app;

                    this.subscribeToAllApis().subscribe((subscriptions) => {
                        this.status = InitStatus.Ready;
                    });

                }, () => {
                    toastService.showMessage(
                        'Unable to get or create subscriptions for user.', {
                            type: 'error',
                            sticky: true
                            }
                    );
                    this.status = InitStatus.Error;
                });
            }
        });

        let sessionRefreshInProgress = false;
        authResourceHandler.getAuthErrors().subscribe((error) => {
            if (!sessionRefreshInProgress) {
                sessionRefreshInProgress = true;
                this.userService.tryRefreshSession().then((success) => {
                    sessionRefreshInProgress = false;
                    if (!success) {
                        confirmDialogService.askConfirmation(
                            'Your session is expired. Would you like to login again?',
                            'Session expired',
                            () => {
                                this.userService.login();
                            },
                            () => {

                            }
                        );
                    }
                }, () => {
                    sessionRefreshInProgress = false;
                });
            }
        });

    }

    subscribeToAllApis() {

        let apiRequest = this.apiService.getApisForTag(this.configService.config.userApisTag);

        let subscriptionsRequest = this.apiService.getSubscriptions(this.appData.id).map((subscriptions) => {
            let apiSubscriptions = {};
            subscriptions.forEach((subscription) => {
                apiSubscriptions[subscription.apiId] = {
                    ...subscription,
                    status: ApiSubscriptionStatus.Subscribed
                };
            });
            return apiSubscriptions;
        });

        return forkJoin([apiRequest, subscriptionsRequest]).map(([apis, subscriptions]) => {
            let missingApis = [];
            apis.forEach((api) => {
                if (!subscriptions[api.id]) {
                    missingApis.push(api);
                }
            });

            if (missingApis.length) {
                return this.apiService.subscribeToApis(this.appData.id, missingApis.map(api => api.id)).subscribe((responses) => {

                    let hasErrors = false;
                    responses.forEach((response: any, idx) => {
                        let apiIdentifier = missingApis[idx].id;
                        if (response.error) {

                            hasErrors = true;

                            subscriptions[apiIdentifier] = {
                                status: ApiSubscriptionStatus.SubscriptionError,
                                error: response.description
                            };
                        } else {
                            subscriptions[apiIdentifier] = {
                                ...response.data,
                                status: ApiSubscriptionStatus.Subscribed
                            };
                        }
                    });

                    if (hasErrors) {
                        this.toastService.showMessage('Error subscribing from some apis', {
                            type: 'error'
                        });
                    }
                });
            }

            return true;
        });

    }

    onLoginClick() {
        this.userService.login();
    }

    onLogoutClick() {
        this.userService.logout();
    }

}



// WEBPACK FOOTER //
// ./src/main/components/root/root.component.ts